// Add to bag feedback, on every add-to-bag button: Horizon's (.add-to-cart-button) and our own that opt in
// with data-sv-atc-button, like the quick add pop-up (loaded site-wide from sections/sivana-brand-styles.liquid;
// styles: assets/sivana-cart.css, "Add to bag feedback").
// Tap: the button says "Adding" with three soft dots while the bag updates, then "Added" with a check after
// it, a small bounce and a few bamboo leaves floating up, while the count circle on the header bag pops.
// After ADDED_MS it goes back to its own label. If the add fails it gives a short shake and goes straight
// back (the page's own error message explains why). Text moves match the approved mockup
// (claude.ai/artifact/APJoUMexN22i2ueCjS5BXd, option 4).
// Driven by Shopify's add event (shopify:cart:lines-update) and its promise, so "Adding" lasts as long as
// the bag takes to update (never shorter than MIN_ADDING_MS, so the dots can be seen).
// Stoq pre-orders: when the terms box isn't ticked, Stoq holds the add and opens its pop-up. Continue sends it;
// closing the pop-up (X, Cancel, backdrop, Escape) makes Stoq answer "OK" with nothing added. So when the pop-up
// was open during an add and Continue wasn't pressed, the button just goes back to its own words, no "Added".
const MIN_ADDING_MS = 650;
const ADDED_MS = 1500;
const RETURN_MS = 420; // the label's trip back down from above
const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
const BUTTONS = '.add-to-cart-button, [data-sv-atc-button]';
const CHECK = '<svg class="sv-atc__check" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3.2 3.2L13 4.8"/></svg>';
const LEAF =
  '<svg viewBox="0 0 14 14" aria-hidden="true"><path d="M1.5 12.5C3 7 7 3 12.5 1.5C11 7 7 11 1.5 12.5Z"/><path d="M1.5 12.5L9 5"/></svg>';

let pressed = null;
let pressedAt = 0;

// Stoq's pre-order pop-up (see above)
let stoqContinuedAt = 0;
document.addEventListener(
  'click',
  (event) => {
    if (event.target.closest?.('#preorder-accept-button')) stoqContinuedAt = performance.now();
  },
  { capture: true }
);
const stoqPopupOpen = () => !!document.querySelector('.stoq-preorder-modal #preorderContainer')?.offsetParent;

// Remember which button the shopper actually pressed (the phone's floating bar presses the page's hidden
// button for them).
const remember = (event) => {
  const button = event.target.closest?.(BUTTONS);
  if (!button || button.disabled) return;
  pressed = button;
  pressedAt = performance.now();
};
document.addEventListener('pointerdown', remember, { capture: true, passive: true });
document.addEventListener(
  'keydown',
  (event) => {
    if (event.key === 'Enter' || event.key === ' ') remember(event);
  },
  { capture: true }
);

function buttonFor(event) {
  if (pressed?.isConnected && performance.now() - pressedAt < 6000) return pressed;
  // Phones: an add from the size sheet belongs to the floating button
  const floating = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__button');
  if (floating?.offsetParent) return floating;
  return event.target?.closest?.(BUTTONS) || event.target?.querySelector?.(BUTTONS) || null;
}

// The button's own words, as shown right now (the phone's floating button draws them from data-sivana-label)
function ownLabel(button) {
  if (button.dataset.sivanaLabel) return button.dataset.sivanaLabel;
  // Pre-orders: the page's button shows "Pre-order" from CSS (assets/sivana-pdp-main.css)
  if (button.closest('.product-information')?.querySelector('[data-sivana-preorder]')) return 'Pre-order';
  const inner = button.querySelector('.add-to-cart-text__content > span');
  if (inner) return inner.textContent.trim();
  return [...button.childNodes]
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .map((node) => node.textContent)
    .join(' ')
    .trim();
}

function prepare(button) {
  let layer = button.querySelector(':scope > .sv-atc');
  if (!layer) {
    layer = document.createElement('span');
    layer.className = 'sv-atc';
    layer.setAttribute('aria-hidden', 'true');
    layer.innerHTML = `<span class="sv-atc__idle"></span><span class="sv-atc__adding">Adding<span class="sv-atc__dots"><i></i><i></i><i></i></span></span><span class="sv-atc__added">Added${CHECK}</span>`;
    button.append(layer);
  }
  layer.querySelector('.sv-atc__idle').textContent = ownLabel(button);
}

function leaves(button) {
  const box = button.getBoundingClientRect();
  // Inside an open pop-up the leaves must live in it too, or they'd fly behind it
  const home = button.closest('dialog[open]') || document.body;
  for (let i = 0; i < 7; i++) {
    const leaf = document.createElement('span');
    leaf.className = 'sv-atc-leaf';
    leaf.innerHTML = LEAF;
    leaf.style.left = `${box.left + box.width * (0.25 + Math.random() * 0.5)}px`;
    leaf.style.top = `${box.top + 6}px`;
    home.append(leaf);
    const dx = (Math.random() - 0.5) * 140;
    const dy = -60 - Math.random() * 60;
    const turn = (Math.random() - 0.5) * 240;
    setTimeout(() => leaf.remove(), 2400); // backup, in case the animation never finishes (hidden tab)
    leaf
      .animate(
        [
          { transform: 'translate(0, 0) rotate(0deg) scale(0.6)', opacity: 0 },
          { opacity: 1, offset: 0.2 },
          { transform: `translate(${dx}px, ${dy}px) rotate(${turn}deg) scale(1)`, opacity: 0 },
        ],
        { duration: 950 + Math.random() * 450, easing: 'cubic-bezier(.22, 1, .36, 1)', delay: i * 35 }
      )
      .finished.finally(() => leaf.remove());
  }
}

function popCount() {
  document.querySelectorAll('cart-icon').forEach((icon) => {
    icon.classList.remove('sv-count-pop');
    void icon.offsetWidth; // restart the animation on a second add
    icon.classList.add('sv-count-pop');
    setTimeout(() => icon.classList.remove('sv-count-pop'), 900);
  });
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
// Two frames, so the browser draws the starting position first (or 50ms, if frames are paused in a hidden tab)
const nextFrame = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
    setTimeout(resolve, 50);
  });

// States on the button (data-sv-atc), in order: start (the layer's copy of the label sits exactly over
// the real one) > adding > added > return (the label comes back down from above) > done (the real label
// takes over again, no fade) > removed. A failed add goes adding > error (shake, label back) > done.
async function play(button, promise) {
  const run = Symbol('add');
  button.svAtcRun = run;
  const current = () => button.svAtcRun === run;

  prepare(button);
  button.dataset.svAtc = 'start';
  await nextFrame();
  if (!current()) return;
  button.dataset.svAtc = 'adding';
  const started = performance.now();
  let sawStoqPopup = stoqPopupOpen();
  const stoqWatch = setInterval(() => {
    if (stoqPopupOpen()) sawStoqPopup = true;
  }, 150);

  let ok = true;
  try {
    const result = await promise;
    if (result?.detail?.didError) ok = false;
  } catch {
    ok = false;
  }
  clearInterval(stoqWatch);
  // Stoq's pop-up was closed without Continue: nothing was added, so no "Added" and no shake
  const stoqClosed = sawStoqPopup && stoqContinuedAt < started;
  await wait(Math.max(0, MIN_ADDING_MS - (performance.now() - started)));
  if (!current()) return;

  if (stoqClosed) {
    button.dataset.svAtc = 'return';
  } else if (ok) {
    button.dataset.svAtc = 'added';
    // Pop-ups wait for this before closing, so the shopper sees the check
    button.dispatchEvent(new CustomEvent('sivana:atc-added', { bubbles: true }));
    navigator.vibrate?.(12);
    popCount();
    if (!reduceMotion.matches) leaves(button);
    await wait(ADDED_MS);
    if (!current()) return;
    button.dataset.svAtc = 'return';
  } else {
    button.dataset.svAtc = 'error';
  }
  await wait(RETURN_MS);
  if (!current()) return;
  button.dataset.svAtc = 'done';
  await nextFrame();
  if (current()) delete button.dataset.svAtc;
}

document.addEventListener('shopify:cart:lines-update', (event) => {
  if (event.action && event.action !== 'add') return;
  const button = buttonFor(event);
  pressed = null;
  if (button?.svAtcPending) return; // already playing, see below
  if (button) play(button, event.promise);
});

// A button whose add can't start right away (the phone size sheet first switches the page to the chosen
// size) shows "Adding" at once: dispatch sivana:atc-pending on it with detail.promise, settled like
// Shopify's add promise. The add event that follows is then part of the same run.
document.addEventListener('sivana:atc-pending', (event) => {
  const button = event.target.closest?.(BUTTONS);
  if (!button) return;
  button.svAtcPending = true;
  play(button, Promise.resolve(event.detail?.promise).finally(() => (button.svAtcPending = false)));
});
